import { Box, Typography, useTheme } from '@mui/material';
import { Formik } from 'formik';
import { DateTime } from 'luxon';
import { type FC, type ReactElement, useContext, useEffect, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  FormikProject,
  NewProjectSchemaFormik,
  type ProjectFormikType,
  type RequiredProjectFieldsInfo,
  formikProjectToWire,
} from '@core/formik/project';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import { NewProject, type Project } from '@core/types/api/Project/Project';
import { FileEntityType } from '@core/types/file';
import { toRecordById } from '@core/utils/domainHelpers';
import { hasValue } from '@core/utils/equality';
import { withCopyFilesFrom } from '@core/utils/files';
import {
  calculateInitialForecasts,
  distributeDeltaToForecasts,
  isForecastedProductCategory,
  toNum,
} from '@core/utils/forecastDistribution';
import { FormErrorNotification } from '@core/utils/formikHelpers';
import { isDefaultUUID } from '@core/utils/uuid';
import { ArchivedPlantWarning } from '@web/components/ArchivedPlantWarning/ArchivedPlantWarning';
import {
  type FileUploadsManager,
  attachBufferedEntityFiles,
  useFileUploads,
} from '@web/components/Files';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useSlabFormQuery } from '@web/hooks/useSlabFormQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { MakeToastBuilderReturn } from '@web/providers/ToastProvider/utils/makeToastBuilder';

import { DrawerFormFooter } from '../../../components/DrawerFormFooter/DrawerFormFooter';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { SlabDrawer } from '../../../components/SlabDrawer/SlabDrawer';
import { useDrawerArchive } from '../../../hooks/useDrawerArchive';
import type { SharedDrawerOverrideProps } from '../../../hooks/useDrawerManager';
import { useSlabMutation } from '../../../hooks/useSlabMutation';
import { useSlabQuery, useSlabQueryFetcher } from '../../../hooks/useSlabQuery';
import { useTenantMargins } from '../../../hooks/useTenantMargins';
import { DefaultEnsureDefined } from '../../../utils/DomainHelpers';
import { ProjectDrawerSections } from './common/ProjectDrawerSections';
import { ProjectSection } from './types';

type ProjectDrawerProps = Omit<SharedDrawerOverrideProps<Project>, 'resourceID' | 'onSave'> & {
  projectID: string | null;
  onSuccess: (project: Project) => void;
  /** When true, projectID is the project being cloned, not edited. @default false */
  isCloning?: boolean;
  /** @default 'Project Information' */
  initialSection?: ProjectSection;
  /** @default DateTime.now().year */
  initialForecastYear?: number;
  /** Field to focus when drawer opens */
  focusField?: string;
  /** New project status ID to set when drawer opens */
  newProjectStatusId?: string;
};

export const attachBufferedProjectFiles = (
  manager: FileUploadsManager,
  projectId: string,
  toast: MakeToastBuilderReturn,
): void => attachBufferedEntityFiles(manager, projectId, toast, FileEntityType.Project);

/**
 * Stamps the revenue override with the first plant's currency, resolved via an
 * awaited (cache-deduped) lookup so a submit cannot race the reactive lookup
 * used while editing. Rejects when the plant cannot be fetched; callers must
 * abort the save in that case, since the currency stamped on the form may
 * belong to a previously selected plant. Exported for testing.
 */
export const stampRevenueOverrideCurrency = async (
  revenueOverride: ProjectFormikType['revenueOverride'],
  firstPlantID: string | null,
  fetchPlant: ReturnType<typeof useSlabQueryFetcher<'GET plant by ID'>>,
): Promise<ProjectFormikType['revenueOverride']> => {
  if (!revenueOverride || firstPlantID === null) {
    return revenueOverride;
  }
  const firstPlant = await fetchPlant('GET plant by ID', { pathParams: { id: firstPlantID } });
  return { ...revenueOverride, currency: firstPlant.currencyCode || 'USD' };
};

type ProjectDrawerPageProps = ProjectDrawerProps & {
  /** Drawer-level buffered upload queue for the Files step in create mode, and the
   * mutations that fire its create-success attach hook. Owned by the outer
   * `ProjectDrawer` (not this component) -- see that component for why. */
  filesManager: FileUploadsManager;
  createNew: ReturnType<typeof useSlabMutation<'POST project'>>;
  updateExisting: ReturnType<typeof useSlabMutation<'PUT project by ID'>>;
};

const ProjectDrawerPage: FC<ProjectDrawerPageProps> = ({
  projectID,
  isOpen,
  setIsOpen,
  isCloning,
  initialSection = ProjectSection.PROJECT_INFORMATION,
  initialForecastYear = DateTime.now().year,
  focusField,
  newProjectStatusId,
  onSuccess,
  onClose,
  ensureDefined = DefaultEnsureDefined,
  filesManager,
  createNew,
  updateExisting,
}) => {
  const ctx = useContext(SlabContext);
  const usesProjectNumberOverride = ctx.userInfo.tenant.useProjectNumberAutogeneration;
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);
  const theme = useTheme();
  const fetchPlant = useSlabQueryFetcher<'GET plant by ID'>();
  const projectToast = useToast({ subjectName: 'project' });
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const requiredFields: RequiredProjectFieldsInfo = {
    estimatedStartDate: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectEstimatedStartDate,
    ]),
    estimatedEndDate: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectEstimatedEndDate,
    ]),
    bidDate: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectBidDate]),
    expirationDate: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectExpirationDate,
    ]),
    city: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectAddressCity,
      Enums.FeatureFlagName.FeatureFlagRequiresProjectAddress,
    ]),
    address: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectAddress]),
    contractors: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectCompany]),
    revenueOverride: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectRevenueOverride,
    ]),
    readyMixQuantityOverride: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectReadyMixQuantityOverride,
    ]),
    segment: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectSegment]),
    taxCode: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectTaxCode]),
    products: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectProducts]),
    config: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresProjectConfig]),
    productDeliveryType: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProductDeliveryType,
    ]),
    lossDetails: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagRequiresProjectLossDetails,
    ]),
    expirationDateShouldBeAfterStartDate: ctx.userInfo.hasFlags([
      Enums.FeatureFlagName.FeatureFlagExpirationDateShouldBeAfterStartDate,
    ]),
  };
  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);

  // We prefer `useRef` here because it allows for immediate mutable changes to the
  // state. If we use `useState` we see "delays" between setting values vs. rendering,
  // which causes form-values to fall out-of-sync with state-values.
  const curProductsRef = useRef<Record<string, Product>>({});

  const isNew = projectID === null;

  const { confirmAndArchive, isArchiveLoading, resolveIsArchived } = useDrawerArchive({
    entity: 'POST archive project by ID',
    getIsArchived: (data) => data.projectStatus?.isArchived ?? false,
    onSuccess: (data) => {
      setIsOpen(false);
      onSuccess(data);
    },
  });

  const {
    isLoading: isLoadingProject,
    isError: isErrorProject,
    data: maybeProject,
  } = useSlabFormQuery(
    'GET project by ID',
    {
      pathParams: {
        id: projectID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const {
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    data: projectProducts,
  } = useSlabFormQuery(
    'GET project products by project ID',
    {
      pathParams: {
        id: projectID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const {
    isLoading: isLoadingForecasts,
    isError: isErrorForecasts,
    data: forecasts,
  } = useSlabFormQuery(
    'GET project forecasts by project ID',
    {
      pathParams: {
        id: projectID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const {
    isLoading: isLoadingCustomFieldDefinitions,
    isError: isErrorCustomFieldDefinitions,
    data: customFieldDefinitionList,
  } = useSlabQuery('GET custom field definitions', {});

  const {
    isLoading: isLoadingStatuses,
    isError: isErrorStatuses,
    data: statusList,
  } = useSlabQuery('GET project statuses', {});

  // Call useTenantSettings before any conditional returns to maintain hook order
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();

  // Once/if project products load, set them at the drawer state level.
  useEffect(() => {
    // When the drawer closes, clear the current state values.
    if (!isOpen) {
      curProductsRef.current = {};
      return;
    }

    // If the project is new, use the project products from context
    if (isNew) {
      const project = ensureDefined(undefined);

      curProductsRef.current = toRecordById(project.products, (pp) => pp.product);
      return;
    }

    // An existing project is being loaded, so handle project products.
    if (isLoadingProducts || isErrorProducts || projectProducts === undefined) {
      return;
    }
    // Merge, not replace: a background refetch must keep products picked in unsaved rows.
    curProductsRef.current = {
      ...toRecordById(projectProducts, (pp) => pp.product),
      ...curProductsRef.current,
    };
  }, [projectProducts, isLoadingProducts, isOpen]);

  const isLoading =
    isLoadingProject ||
    isLoadingProducts ||
    isLoadingForecasts ||
    isLoadingCustomFieldDefinitions ||
    isLoadingStatuses;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isError =
    isErrorProject ||
    isErrorProducts ||
    isErrorForecasts ||
    isErrorCustomFieldDefinitions ||
    isErrorStatuses;
  const isDataUndefined = customFieldDefinitionList === undefined;

  if (isError || isDataUndefined) {
    return <div>ERROR</div>;
  }

  const { defaultProjectValidityDays, defaultProjectBidDateDays } = ctx.userInfo.tenant;
  const defaultExpirationDate =
    isNew && hasValue(defaultProjectValidityDays)
      ? DateTime.now().plus({ days: defaultProjectValidityDays })
      : null;
  const defaultBidDate =
    isNew && hasValue(defaultProjectBidDateDays)
      ? DateTime.now().plus({ days: defaultProjectBidDateDays })
      : null;

  const project = ensureDefined(
    maybeProject === undefined || projectProducts === undefined
      ? NewProject({
          expirationDate: defaultExpirationDate,
          bidDate: defaultBidDate,
          customFields: customFieldDefinitionList.items
            .filter((cfd) => cfd.defaultValue !== null)
            .map((cfd) => ({
              definition: cfd,
              value: cfd.defaultValue,
            })),
        })
      : NewProject({
          ...maybeProject,
          products: projectProducts,
          forecasts,
        }),
  );
  // if we have a new project and the tenant uses
  // project number overrides, show an example project number
  let { projectNumber } = project;
  if (isNew && usesProjectNumberOverride) {
    projectNumber = DateTime.now().toFormat('yyyy-MM-####');
  }

  // NewProject always builds a zero User, which renders as a selected blank option.
  const user = isNew ? ctx.userInfo.userIfRoles([Enums.RoleName.Salesperson]) : project.user;

  const isArchived = resolveIsArchived(maybeProject?.projectStatus?.isArchived ?? false);

  const formikProject = FormikProject(
    { ...project, projectNumber, user },
    customFieldDefinitionList.items,
    usesDispatchCustomer,
    defaultIsMoneySwitchOn,
    defaultMargins,
    marginConfiguration,
    true,
  );

  const isNewTitle = isDefaultUUID(formikProject.id);

  return (
    <Formik
      validationSchema={NewProjectSchemaFormik(requiredFields, {
        fullContactInfo: requiresContactInfo,
        requiresEmail: requiresContactEmail,
      })}
      initialValues={formikProject}
      // Show pre-existing "UOM issue" product errors before interacting with that part of the form.
      validateOnMount={!isNew}
      initialTouched={{
        productsByPlants: formikProject.productsByPlants?.map((plantGroup) => ({
          products: plantGroup.products.map(() => ({
            product: {
              option: true,
              incompatibleMixQuantityUnits: true,
              allowIncompatibleMixQuantityUnits: true,
            },
          })),
        })),
      }}
      onSubmit={async (values): Promise<void> => {
        const projectValues = { ...values };

        // Only include quantities from forecastable product categories
        // (Mix/Aggregate/Asphalt). Non-forecastable products (Resale, Precast,
        // Other, Charge) must not contribute to totalProducts; otherwise the
        // mismatch check below would incorrectly redistribute their volume
        // into the forecasts on save.
        const totalProducts =
          values.productsByPlants?.reduce(
            (sum, plant) =>
              sum +
              plant.products.reduce(
                (acc, p) =>
                  isForecastedProductCategory(p.product?.category) ? acc + toNum(p.quantity) : acc,
                0,
              ),
            0,
          ) || 0;

        const totalForecasts = values.forecasts?.reduce((sum, f) => sum + toNum(f.value), 0) || 0;

        const hasMismatch = Math.abs(totalProducts - totalForecasts) >= 0.01;
        const isNewProject = isDefaultUUID(formikProject.id);
        const hasManualData = values.forecasts.some((f) => toNum(f.value) !== 0);

        if (isNewProject && !hasManualData) {
          projectValues.forecasts = calculateInitialForecasts(values);
        } else if (hasMismatch) {
          projectValues.forecasts = distributeDeltaToForecasts(values.forecasts, totalProducts, []);
        }

        const firstPlantID = values.productsByPlants?.[0]?.plant?.id ?? null;
        try {
          projectValues.revenueOverride = await stampRevenueOverrideCurrency(
            projectValues.revenueOverride,
            firstPlantID,
            fetchPlant,
          );
        } catch {
          // Without the plant we cannot know the override's currency, and the
          // stamped value may belong to a previously selected plant. Abort the
          // save rather than persist a potentially wrong code.
          projectToast.error({
            message:
              'Could not resolve the location currency for the Revenue Override. Please try saving again.',
          });
          return;
        }

        const wireProject = formikProjectToWire(projectValues);

        const { cloneSourceEntityId, excludedCloneFileIds } = filesManager;

        if (isNewProject) {
          createNew.mutate({
            args: {
              body: hasValue(cloneSourceEntityId)
                ? withCopyFilesFrom(wireProject, cloneSourceEntityId, excludedCloneFileIds)
                : wireProject,
            },
          });
        } else {
          updateExisting.mutate({
            args: {
              pathParams: {
                id: formikProject.id,
              },
              body: wireProject,
            },
          });
        }
      }}
    >
      {(formikBag): ReactElement => {
        const hasSetStatus = useRef(false);
        const prevStartRef = useRef(formikBag.values.estimatedStartDate);
        const prevEndRef = useRef(formikBag.values.estimatedEndDate);

        // When start/end date changes, redistribute ALL forecast categories evenly
        // across the new date range. Runs here (not in ForecastsSection) so it
        // executes once for the whole form regardless of which tab is active.
        useEffect(() => {
          const newStart = formikBag.values.estimatedStartDate;
          const newEnd = formikBag.values.estimatedEndDate;

          const changed = newStart !== prevStartRef.current || newEnd !== prevEndRef.current;
          prevStartRef.current = newStart;
          prevEndRef.current = newEnd;

          if (!changed) return;
          if (!newStart || !newEnd) return;

          const parsedStart = DateTime.fromISO(newStart);
          const parsedEnd = DateTime.fromISO(newEnd);
          if (!parsedStart.isValid || !parsedEnd.isValid) return;

          const {
            forecasts: currentForecasts,
            productsByPlants,
            forecastDistributionMethod,
            readyMixQuantityOverride,
            aggregateQuantityOverride,
            asphaltQuantityOverride,
          } = formikBag.values;

          const isInRange = (intervalStart: string): boolean => {
            const d = DateTime.fromISO(intervalStart.replace(/T.*$/, ''));
            return d >= parsedStart.startOf('month') && d <= parsedEnd.startOf('month');
          };

          const getOverrideByCategory = (category: Enums.ProductCategory): number | null => {
            if (category === Enums.ProductCategory.ReadyMix)
              return readyMixQuantityOverride ?? null;
            if (category === Enums.ProductCategory.Aggregate)
              return aggregateQuantityOverride ?? null;
            if (category === Enums.ProductCategory.Asphalt) return asphaltQuantityOverride ?? null;
            return null;
          };

          const getTotal = (category: Enums.ProductCategory): number => {
            const override = getOverrideByCategory(category);
            if (typeof override === 'number' && override > 0) return override;
            return productsByPlants.reduce((sum, plant) => {
              return (
                sum +
                plant.products.reduce((s, p) => {
                  if (p.product?.category !== category) return s;
                  return s + toNum(p.quantity);
                }, 0)
              );
            }, 0);
          };

          const categories = [
            Enums.ProductCategory.ReadyMix,
            Enums.ProductCategory.Aggregate,
            Enums.ProductCategory.Asphalt,
          ].filter((cat) => currentForecasts.some((f) => f.productCategory === cat));

          const method = forecastDistributionMethod ?? 'monthly';

          const updated = currentForecasts.map((f) => {
            if (!categories.includes(f.productCategory as Enums.ProductCategory)) return f;
            if (!isInRange(f.intervalStart)) return { ...f, value: '' };

            // Temporarily mark as in-range; we'll fill actual values below
            return { ...f, value: '__in_range__' };
          });

          categories.forEach((category) => {
            const total = getTotal(category);
            if (total <= 0) return;

            const inRangeEntries = updated
              .map((f, i) => ({ f, i }))
              .filter(
                ({ f }) => f.productCategory === category && (f.value as string) === '__in_range__',
              );

            if (inRangeEntries.length === 0) return;

            const base = Math.floor(total / inRangeEntries.length);
            const remainder = total % inRangeEntries.length;

            if (method === 'monthly') {
              inRangeEntries.forEach(({ i }, idx) => {
                updated[i] = { ...updated[i], value: String(base + (idx < remainder ? 1 : 0)) };
              });
            } else {
              // Quarterly
              const quarters: Record<number, typeof inRangeEntries> = {};
              inRangeEntries.forEach((entry) => {
                const d = DateTime.fromISO(entry.f.intervalStart.replace(/T.*$/, ''));
                const q = Math.floor((d.month - 1) / 3);
                if (!quarters[q]) quarters[q] = [];
                quarters[q].push(entry);
              });
              const qKeys = Object.keys(quarters).length;
              const baseQ = Math.floor(total / qKeys);
              const remQ = total % qKeys;
              Object.values(quarters).forEach((qEntries, qi) => {
                const qTotal = baseQ + (qi < remQ ? 1 : 0);
                const baseM = Math.floor(qTotal / qEntries.length);
                const remM = qTotal % qEntries.length;
                qEntries.forEach(({ i }, mi) => {
                  updated[i] = {
                    ...updated[i],
                    value: String(baseM + (mi < remM ? 1 : 0)),
                  };
                });
              });
            }
          });

          // Clear any remaining placeholder
          const final = updated.map((f) =>
            (f.value as string) === '__in_range__' ? { ...f, value: '' } : f,
          );

          formikBag.setFieldValue('forecasts', final);
        }, [formikBag.values.estimatedStartDate, formikBag.values.estimatedEndDate]);

        useEffect(() => {
          if (hasValue(newProjectStatusId) && !hasSetStatus.current) {
            const systemBehavior =
              statusList?.items.find((s) => s.id === newProjectStatusId)?.systemBehavior ?? null;
            formikBag.setFieldValue('projectStatus', { id: newProjectStatusId, systemBehavior });
            hasSetStatus.current = true;
          }
        }, [newProjectStatusId]);

        useEffect(() => {
          if (!isOpen) {
            hasSetStatus.current = false;
          }
        }, [isOpen]);

        const initialPlantIds = formikBag.initialValues.productsByPlants
          .map((p) => p.plant.id)
          .filter((id) => hasValue(id));
        const currentPlantIds = formikBag.values.productsByPlants
          .map((p) => p.plant.id)
          .filter((id) => hasValue(id));

        const hasPlantChanged = initialPlantIds.join(',') !== currentPlantIds.join(',');

        const closeDrawer = (): void => {
          formikBag.resetForm({ values: formikProject });
          setIsOpen(false);
          onClose?.();
        };
        const requestClose = (): void =>
          guardClose({
            isDirty:
              !isArchived &&
              (editTracker.isEdited() ||
                filesManager.hasAttachableItems ||
                filesManager.hasPendingUploads ||
                filesManager.excludedCloneFileIds.length > 0),
            canSave: formikBag.isValid,
            isSaving: createNew.isPending || updateExisting.isPending || formikBag.isSubmitting,
            onSave: formikBag.submitForm,
            onDiscard: closeDrawer,
          });

        return (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
            <Box>
              <FormErrorNotification />
              <SlabDrawerHeader
                title={`${isNewTitle ? 'Create ' : 'Edit '}a project`}
                onClose={requestClose}
              />
              {!isNew && maybeProject?.plants.some((pp) => pp.plant.isArchived) && (
                <ArchivedPlantWarning entityName='project' />
              )}
              <ProjectDrawerSections
                initialSection={initialSection}
                initialForecastYear={initialForecastYear}
                curProductsRef={curProductsRef}
                omitCustomFieldSection={customFieldDefinitionList.items.length === 0}
                focusField={focusField}
                requiredFields={requiredFields}
                filesManager={filesManager}
              />
            </Box>
            <DrawerFormFooter
              isArchived={isArchived}
              isNew={isNew}
              hasEntity={maybeProject !== undefined}
              isArchiveLoading={isArchiveLoading}
              isSaving={createNew.isPending || updateExisting.isPending}
              isSaveDisabled={
                (hasValue(isCloning) ? !isCloning : true) &&
                (!formikBag.dirty || createNew.isPending || updateExisting.isPending)
              }
              isResetDisabled={
                !formikBag.dirty ||
                createNew.isPending ||
                updateExisting.isPending ||
                isArchiveLoading
              }
              onArchive={(): void => {
                if (maybeProject)
                  confirmAndArchive({
                    id: maybeProject.id,
                    isArchived,
                    // The drawer doesn't have a confirmation UI for cascading to quotes (the details page does).
                    body: { applyToQuotes: false },
                  });
              }}
              onCancel={requestClose}
              onReset={(): void => formikBag.resetForm({ values: formikProject })}
              onSave={formikBag.submitForm}
            />
            {/* If the project is not new and the plant has changed, display this warning. */}
            {hasPlantChanged && (
              <Box paddingY='0.5rem'>
                <Typography variant='h6' color={theme.palette.warning.main}>
                  Warning: The plant(s) for this project have changed. Are you sure?
                </Typography>
              </Box>
            )}
            <Typography marginTop='12px'>
              To save, address any highlighted issues in red first.
            </Typography>
          </SlabDrawerLayout>
        );
      }}
    </Formik>
  );
};

export const ProjectDrawer = (props: ProjectDrawerProps): ReactElement => {
  const { isOpen, setIsOpen, onClose, onSuccess, onError } = props;

  const filesManager = useFileUploads({
    entityType: FileEntityType.Project,
    entityId: null,
    cloneSourceEntityId: props.isCloning === true ? props.projectID : null,
  });
  const filesToast = useToast({ subjectName: 'file' });

  const createNew = useSlabMutation('POST project', {
    onSuccess: (createdProject): void => {
      attachBufferedProjectFiles(filesManager, createdProject.id, filesToast);
      onSuccess(createdProject);
    },
    onError,
  });
  const updateExisting = useSlabMutation('PUT project by ID', {
    onSuccess,
    onError,
  });

  useEffect(() => {
    if (!isOpen) {
      filesManager.reset();
    }
  }, [isOpen]);

  return (
    <SlabDrawer
      isOpen={isOpen}
      close={() => {
        setIsOpen(false);
        onClose?.();
      }}
      paperProps={{
        sx: {
          width: '80rem',
          maxWidth: '100%',
        },
      }}
    >
      <ProjectDrawerPage
        {...props}
        filesManager={filesManager}
        createNew={createNew}
        updateExisting={updateExisting}
      />
    </SlabDrawer>
  );
};
